import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import {
  useAnalyticsOrgLocations,
  DemoDataBanner,
  MultiLocationLargeModeOnlyFeature,
  LargeModeOnlyFeature,
  WeekDayFilterSelector,
  GeneralFeedbackButton,
} from '@frontend/analytics-shared';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { useIsSmallMode } from '@frontend/shared';
import { useNavigate, type AnyRoute } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Tabs, useModalControl } from '@frontend/design-system';
import { DataSyncInProgress } from '../components/practice/data-sync-in-progress';
import { useIsPADemoAccount } from '../components/practice/hooks/use-is-pa-demo-account';
import { usePracticeAnalyticsDemoData } from '../components/practice/hooks/use-practice-analytics-demo-data';
import { usePracticeAnalyticsShallowStore } from '../components/practice/hooks/use-practice-analytics-store';
import { MorningHuddleTabPanel } from '../components/practice/morning-huddle-tab-panel';
import { OpportunitiesListTabPanel } from '../components/practice/opportunities-list-tab-panel';
import { PracticeAnalyticsFilters } from '../components/practice/practice-analytics-filters';
import { PracticeChartsView } from '../components/practice/practice-charts-view';
import { URLs } from '../constants';
import { trackingIds } from '../tracking-ids';

const initialTab = 'overview';

export const PracticeAnalytics = () => {
  const { t } = useTranslation('analytics');
  const isDemoAccount = useIsPADemoAccount();
  const navigate = useNavigate();
  const { isSmallMode } = useIsSmallMode();
  const { hasDataSynced, isLoadingLocations, locationIds } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'PA',
  });
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [reportDate, setReportDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [isFetchingChartsData, setIsFetchingChartsData] = useState<boolean>();
  const morningHuddleModal = useModalControl();
  const { filters, isPageReady, setDemoData, setIsDemoAccount, setIsPageReady, showDemoChipAndBanner } =
    usePracticeAnalyticsShallowStore(
      'filters',
      'isPageReady',
      'setDemoData',
      'setIsDemoAccount',
      'setIsPageReady',
      'showDemoChipAndBanner'
    );
  const demoData = usePracticeAnalyticsDemoData();

  const renderMorningHuddlePageAction = () => (
    <div css={styles.morningHuddleActions}>
      <WeekDayFilterSelector onChange={setReportDate} value={reportDate} />
      <Button
        {...morningHuddleModal.triggerProps}
        iconName='settings'
        label={t('Edit Users')}
        size='large'
        trackingId={trackingIds.practiceAnalytics.editMHUsersButton}
        variant='secondary'
      />
    </div>
  );

  useEffect(() => {
    setDemoData(demoData);
  }, [demoData]);

  useEffect(() => {
    setIsDemoAccount(isDemoAccount);
  }, [isDemoAccount]);

  useEffect(() => {
    setIsPageReady(true);
  }, []);

  return (
    <>
      {showDemoChipAndBanner && (
        <DemoDataBanner
          onCTAClick={() => navigate({ to: URLs.PRACTICE_ANALYTICS_SUBSCRIBE as AnyRoute })}
          startTrialTrackingId={trackingIds.practiceAnalyticsPLG.plgFreeTrialFromDemo}
          subtitle={t("As a subscriber, you'll see your own practice's data updated daily.")}
          title={t("You're taking Practice Analytics for a test drive with demo data!")}
        />
      )}
      <Page css={styles.page}>
        <Page.Header>
          <Page.Header.Heading>
            <Page.Header.Title title={<Header title={t('Practice Analytics')} />} />
            <Page.Header.Action>
              <span css={styles.morningHuddleActions}>
                <GeneralFeedbackButton selectedLocationFromPageFilter={filters.locations} />
                {activeTab === 'morning-huddle' && renderMorningHuddlePageAction()}
              </span>
            </Page.Header.Action>
          </Page.Header.Heading>
        </Page.Header>

        <Page.Body>
          {hasDataSynced || isDemoAccount ? (
            <Tabs initialTab={initialTab} onChange={setActiveTab}>
              <Tabs.Bar css={styles.tabBar}>
                <Tabs.Tab id='overview' trackingId={trackingIds.practiceAnalytics.overviewTab}>
                  {t('Overview')}
                </Tabs.Tab>
                <Tabs.Tab id='opportunities' trackingId={trackingIds.practiceAnalytics.opportunitiesTab}>
                  {t('Opportunity List')}
                </Tabs.Tab>
                <Tabs.Tab id='morning-huddle' trackingId={trackingIds.practiceAnalytics.morningHuddleTab}>
                  {t('Morning Huddle')}
                </Tabs.Tab>
              </Tabs.Bar>

              <Tabs.Panel controller='overview'>
                <PracticeAnalyticsFilters isLoadingData={isFetchingChartsData} />
                {isPageReady && activeTab === 'overview' && (
                  <PracticeChartsView onFetchStateChange={setIsFetchingChartsData} />
                )}
              </Tabs.Panel>

              <Tabs.Panel controller='opportunities'>
                {isSmallMode ? (
                  <LargeModeOnlyFeature featureName={t('Opportunity List')} />
                ) : (
                  isPageReady && <OpportunitiesListTabPanel />
                )}
              </Tabs.Panel>

              <Tabs.Panel controller='morning-huddle'>
                {isSmallMode ? (
                  <LargeModeOnlyFeature featureName={t('Morning Huddle')} />
                ) : (
                  isPageReady &&
                  activeTab === 'morning-huddle' && (
                    <MorningHuddleTabPanel reportDate={reportDate} settingModalProps={morningHuddleModal.modalProps} />
                  )
                )}
              </Tabs.Panel>
            </Tabs>
          ) : (
            <DataSyncInProgress />
          )}
          <ContentLoader show={!isPageReady} />
          <MultiLocationLargeModeOnlyFeature locationCount={locationIds?.length} />
        </Page.Body>
      </Page>
      <ContentLoader show={isLoadingLocations} />
    </>
  );
};

const styles = {
  page: css`
    overflow: auto;
  `,

  tabBar: css`
    margin-bottom: ${theme.spacing(2)};
  `,

  morningHuddleActions: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
  `,
};
